import { useState, useEffect, useCallback } from "react";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Bell, BellOff, BellRing } from "lucide-react";
import logger from "@/lib/logger";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// U06a — same steps as the banner's requestPermission, callable from Settings.
// Returns "granted" | "denied" | "unsupported" | "error".
export async function enablePushOnThisDevice() {
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  const result = await Notification.requestPermission();
  if (result !== "granted") return result;
  try {
    const { public_key: vapidKey } = await api.getVapidKey();
    if (!vapidKey) return "error";
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidKey) });
    }
    await api.subscribePush({
      subscription: sub.toJSON(),
      device_name: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? "Mobile" : "Desktop",
    });
    return "granted";
  } catch (err) {
    logger.error("Push subscription failed:", err);
    return "error";
  }
}


export default function PushPermission() {
  const { tokens: t } = useTheme();
  const { user } = useAuth();
  const [permission, setPermission] = useState("default");
  const [showBanner, setShowBanner] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("mmntm-push-dismissed") === "true"
  );

  useEffect(() => {
    if (!user) return;
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
    setPermission(Notification.permission);

    // Fetch VAPID key
    api.getVapidKey().then((res) => {
      if (res.enabled && res.public_key) {
        setVapidKey(res.public_key);
        if (Notification.permission === "default" && !dismissed) {
          setTimeout(() => setShowBanner(true), 5000);
        }
      }
    }).catch((e) => logger.error("getVapidKey:", e));
  }, [user, dismissed]);

  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return;
    const result = await Notification.requestPermission();
    setPermission(result);
    setShowBanner(false);

    if (result === "granted" && vapidKey) {
      try {
        const reg = await navigator.serviceWorker.ready;
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
          });
        }
        await api.subscribePush({
          subscription: sub.toJSON(),
          device_name: /Mobile|Android|iPhone/i.test(navigator.userAgent)
            ? "Mobile"
            : "Desktop",
        });
      } catch (err) {
        logger.error("Push subscription failed:", err);
      }
    }
  }, [vapidKey]);

  const dismissBanner = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem("mmntm-push-dismissed", "true");
  };

  if (!user || !showBanner) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] max-w-xs rounded-xl border shadow-2xl p-4"
      style={{ background: t.bgSecondary, borderColor: t.borderColor }}
      data-testid="push-permission-banner"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${t.accent}15` }}
        >
          <BellRing className="h-4 w-4" style={{ color: t.accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold" style={{ color: t.textPrimary }}>
            Enable Notifications
          </p>
          <p className="text-[10px] mt-0.5" style={{ color: t.textMuted }}>
            Get reminders for streaks, quests, and focus sessions. Never miss your momentum.
          </p>
          <div className="flex gap-2 mt-2.5">
            <button
              onClick={requestPermission}
              className="text-[10px] font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={{ background: t.accent, color: t.bgPrimary }}
              data-testid="push-enable-btn"
            >
              Enable
            </button>
            <button
              onClick={dismissBanner}
              className="text-[10px] font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={{ color: t.textMuted, background: `${t.textMuted}10` }}
              data-testid="push-dismiss-btn"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PushSettingsToggle() {
  const { tokens: t } = useTheme();
  const [permission, setPermission] = useState("default");

  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const toggle = async () => {
    if (permission === "granted") return;
    if (!("Notification" in window)) return;
    const result = await Notification.requestPermission();
    setPermission(result);
  };

  const Icon = permission === "granted" ? Bell : permission === "denied" ? BellOff : BellRing;
  const label =
    permission === "granted"
      ? "Notifications enabled"
      : permission === "denied"
        ? "Notifications blocked"
        : "Enable notifications";

  return (
    <button
      onClick={toggle}
      className="flex items-center gap-2 px-3 py-2 rounded-lg w-full text-left transition-all"
      style={{
        background: permission === "granted" ? `${t.success}08` : `${t.textMuted}08`,
        border: `1px solid ${permission === "granted" ? t.success : t.borderColor}20`,
      }}
      disabled={permission === "denied"}
      data-testid="push-settings-toggle"
    >
      <Icon
        className="h-3.5 w-3.5"
        style={{ color: permission === "granted" ? t.success : t.textMuted }}
      />
      <span
        className="text-[11px] font-medium"
        style={{ color: permission === "granted" ? t.success : t.textMuted }}
      >
        {label}
      </span>
    </button>
  );
}
